import { isActiveGroup } from 'sonata-static-storage/lib/helpers/itemsGrouping.helpers';
import { getDefaultScreenGroup } from 'sonata-static-storage/lib/schemas/campaign/screenGroup.schema';

import { toId } from './groupIds';

import type { ScreenGroupInterface } from 'sonata-static-storage/lib/schemas/campaign/screenGroup.schema';
import type { VenueGroupExtended, ZoneGroupExtended } from '../../multiFlightMap.types';
import type { GroupableItem, GroupFamily, GroupLike, NewGroupBase } from './grouping.types';

/** Concrete group type stored for each family. */
export interface FamilyGroupMap {
  locations: ZoneGroupExtended;
  screens: ScreenGroupInterface<string>;
  venues: VenueGroupExtended;
}

export interface FamilyAdapter {
  /** Ad field referencing this family's groups (`null`: never referenced by ads / temporal pacing). */
  adGroupKey: 'screenGroups' | 'zoneGroups' | null;
  /** Group members fall back to this group when left without any active group (screens only). */
  defaultGroupId: (groups: GroupLike[]) => string | undefined;
  /** UI id of an item (screens are keyed by `source_element_id`). */
  itemId: (item: GroupableItem) => string;
  /** Groups live on the user, not on the entity: draft status is irrelevant for deletion. */
  userLevel: boolean;
}

const noDefaultGroup = (): undefined => undefined;
const idOf = (item: GroupableItem): string => toId(item._id);

export const familyAdapters: Record<GroupFamily, FamilyAdapter> = {
  locations: {
    adGroupKey: 'zoneGroups',
    defaultGroupId: noDefaultGroup,
    itemId: idOf,
    userLevel: false
  },
  screens: {
    adGroupKey: 'screenGroups',
    defaultGroupId: groups => {
      const defaultGroup = groups.find(group => group.groupType === 'default' && isActiveGroup(group));
      return defaultGroup ? toId(defaultGroup._id) : undefined;
    },
    itemId: item => toId(item.source_element_id),
    userLevel: false
  },
  venues: {
    adGroupKey: null,
    defaultGroupId: noDefaultGroup,
    itemId: idOf,
    userLevel: true
  }
};

/** Builds a brand-new group of the given family with its family-specific defaults. */
export function buildNewGroup<F extends GroupFamily>(family: F, base: NewGroupBase): FamilyGroupMap[F];
export function buildNewGroup(family: GroupFamily, base: NewGroupBase): FamilyGroupMap[GroupFamily] {
  switch (family) {
    case 'screens':
      return { ...base, groupType: 'custom', icon: 'fa-tv' };
    case 'venues':
      return { ...base, groupType: 'custom', icon: 'fa-location-dot' };
    default:
      return { ...base, attribution: false, smart: false };
  }
}

/** Screen groups plus an "UnGrouped" default group when none is active (same list when there is one). */
export function ensureDefaultScreenGroup(groups: ScreenGroupInterface<string>[]): ScreenGroupInterface<string>[] {
  return familyAdapters.screens.defaultGroupId(groups) === undefined ? [...groups, getDefaultScreenGroup()] : groups;
}
